import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { LogOut, User, Briefcase, CheckCircle, Clock, AlertCircle, Send, Paperclip, FileText, X, MessageSquare, CheckSquare, XCircle } from 'lucide-react';


const NewPortalDashboard = () => {
  const { team_id } = useParams();
  const navigate = useNavigate();
  
  // Process hash auth IMMEDIATELY on component load (before any effects)
  // This prevents race conditions with redirect logic
  const getInitialAuthData = () => {
    // First check URL hash for fresh auth data
    const hash = window.location.hash;
    if (hash && hash.startsWith('#auth=')) {
      try {
        console.log('🔐 Portal Dashboard: Processing auth hash on init...');
        const encodedData = hash.replace('#auth=', '');
        const portalData = JSON.parse(atob(encodedData));
        
        // Store in localStorage immediately
        localStorage.setItem('portal_token', portalData.token);
        localStorage.setItem('portal_team_id', portalData.team_id);
        localStorage.setItem('portal_contact', JSON.stringify(portalData.contact));
        localStorage.setItem('portal_client', JSON.stringify(portalData.client));
        
        // Clear hash from URL (do this after storing to localStorage)
        window.history.replaceState(null, '', window.location.pathname);
        
        console.log('🔐 Portal Dashboard: Hash auth processed successfully');
        return { 
          token: portalData.token,
          contact: portalData.contact, 
          client: portalData.client,
          fromHash: true 
        };
      } catch (e) {
        console.error('🔐 Portal Dashboard: Failed to parse hash auth:', e);
      }
    }
    
    // Fallback: check localStorage for existing session
    const token = localStorage.getItem('portal_token');
    if (token) {
      try {
        const storedContact = localStorage.getItem('portal_contact');
        const storedClient = localStorage.getItem('portal_client');
        console.log('🔐 Portal Dashboard: Found existing session in localStorage');
        return {
          token,
          contact: storedContact ? JSON.parse(storedContact) : null,
          client: storedClient ? JSON.parse(storedClient) : null,
          fromHash: false
        };
      } catch (e) {
        console.error('🔐 Portal Dashboard: Failed to parse localStorage:', e);
      }
    }
    
    console.log('🔐 Portal Dashboard: No auth found');
    return null;
  };
  
  // Get initial auth data (runs once during component initialization)
  const initialAuthData = getInitialAuthData();
  
  // If no auth at all, redirect immediately before rendering
  if (!initialAuthData) {
    console.log('🔐 Portal Dashboard: No auth, will redirect to login');
  }
  
  const [loading, setLoading] = useState(!initialAuthData);
  const [activeTab, setActiveTab] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [contact, setContact] = useState(initialAuthData?.contact || null);
  const [client, setClient] = useState(initialAuthData?.client || null);
  const [error, setError] = useState('');
  const [portalSettings, setPortalSettings] = useState(null);
  const [clientAllowedRequestTypes, setClientAllowedRequestTypes] = useState(null);
  const [clientCustomFieldOptions, setClientCustomFieldOptions] = useState({});
  const [clientCustomFieldValues, setClientCustomFieldValues] = useState({});
  const [referenceTableOptions, setReferenceTableOptions] = useState({});
  const [branding, setBranding] = useState(null);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  
  // Resizable divider state for deliverable modal
  const [dividerPosition, setDividerPosition] = useState(75); // Start at 75% (3/4)
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  
  // Task approval state
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [taskToAction, setTaskToAction] = useState(null);
  const [submittingAction, setSubmittingAction] = useState(false);
  
  // Deliverable comment state
  const [deliverableComment, setDeliverableComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [videoTimestamp, setVideoTimestamp] = useState(null);
  const videoRef = React.useRef(null);
  
  const makeRequestItem = () => ({
    id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    dynamic_fields: {}
  });

  // Request form state
  const [requestForm, setRequestForm] = useState({
    request_type: '',
    title: '',
    description: '',
    dynamic_fields: {},
    items: [makeRequestItem()],
    attachments: []
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState('');

  useEffect(() => {
    // Check if we have valid auth (either from hash or localStorage)
    const token = localStorage.getItem('portal_token');
    console.log('🔐 Portal Dashboard useEffect: token exists:', !!token, 'contact:', !!contact, 'client:', !!client);
    
    if (!token) {
      console.log('🔐 Portal Dashboard: No token found, redirecting to login');
      navigate(`/portal/${team_id}`);
      return;
    }
    
    // We have auth - fetch data
    console.log('🔐 Portal Dashboard: Auth valid, fetching data...');
    setLoading(false);
    fetchTasks();
    fetchPortalSettings();
    fetchBranding();
    
    // Fetch client-specific allowed request types if we have client
    if (client?.id && client.id !== 'preview-demo') {
      fetchClientAllowedRequestTypes(client.id);
      fetchClientCustomFieldOptions();
    }
  }, [team_id, navigate, client]);

  const fetchBranding = async () => {
    try {
      const response = await api.get(`/public/branding/${team_id}`);
      if (response.data) {
        setBranding(response.data);
        // Apply custom colors as CSS variables
        if (response.data.primary_color) {
          document.documentElement.style.setProperty('--portal-primary', response.data.primary_color);
        }
        if (response.data.secondary_color) {
          document.documentElement.style.setProperty('--portal-secondary', response.data.secondary_color);
        }
      }
    } catch (err) {
      console.error('Error fetching branding:', err);
    }
  };

  // Helper to get branded gradient style
  const getBrandGradient = () => {
    if (branding?.primary_color && branding?.secondary_color) {
      return `linear-gradient(to right, ${branding.primary_color}, ${branding.secondary_color})`;
    }
    return 'linear-gradient(to right, rgb(147, 51, 234), rgb(219, 39, 119))';
  };

  // Helper to get primary brand color
  const getPrimaryColor = () => branding?.primary_color || '#9333ea';

  const fetchPortalSettings = async () => {
    try {
      const response = await api.get(`/public/portal/settings/${team_id}`);
      
      if (response.data) {
        setPortalSettings(response.data);
      }
    } catch (err) {
      console.error('Error fetching portal settings:', err);
      // Use default settings if fetch fails
      setPortalSettings({
        enable_request_form: true,
        show_task_description: true,
        show_task_due_date: true,
        show_task_attachments: true,
        show_task_comments: true,
        show_task_progress: true,
        allow_client_approval: false,
        allow_client_revisions: false,
        request_types: ['Bug Report', 'Feature Request', 'General Inquiry'],
        form_title: 'Submit a Request',
        form_description: 'Use this form to submit new requests or feedback to our team.',
        form_fields: [
          { id: 1, name: 'title', label: 'Title', type: 'text', required: true },
          { id: 2, name: 'description', label: 'Description', type: 'textarea', required: true },
          { id: 3, name: 'attachments', label: 'Attachments', type: 'file', required: false }
        ],
        request_type_form_configs: {},
        request_form_schema_version: 1,
        stage_mappings: {
          in_progress_stages: [],
          in_review_stages: [],
          complete_stages: []
        }
      });
    }
  };

  const fetchClientAllowedRequestTypes = async (clientId) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await api.get(`/portal/${team_id}/allowed-request-types/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data?.success) {
        setClientAllowedRequestTypes(response.data.allowed_request_types);
      }
    } catch (err) {
      console.error('Error fetching client allowed request types:', err);
      // Fallback: use all company request types (will be filtered by portalSettings.request_types)
      setClientAllowedRequestTypes(null);
    }
  };

  const fetchClientCustomFieldOptions = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await api.get(`/portal/${team_id}/client-custom-field-options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data?.success) {
        setClientCustomFieldOptions(response.data.options_by_field || {});
        setClientCustomFieldValues(response.data.values_by_field || {});
        setReferenceTableOptions(response.data.options_by_reference_table || {});
      }
    } catch (err) {
      console.error('Error fetching client custom field options:', err);
      setClientCustomFieldOptions({});
      setClientCustomFieldValues({});
      setReferenceTableOptions({});
    }
  };

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await api.get(`/portal/${team_id}/tasks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setTasks(response.data.tasks);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      if (err.response?.status === 401) {
        // Token expired or invalid
        handleLogout();
      } else {
        setError('Failed to load tasks. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTask = async (taskId) => {
    setTaskToAction(taskId);
    setShowApproveConfirm(true);
  };

  const confirmApproveTask = async () => {
    try {
      setSubmittingAction(true);
      const token = localStorage.getItem('portal_token');
      
      await api.post(
        `/portal/${team_id}/tasks/${taskToAction}/approve`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Refresh tasks
      await fetchTasks();
      setShowApproveConfirm(false);
      setTaskToAction(null);
      alert('Task approved successfully!');
    } catch (error) {
      console.error('Error approving task:', error);
      alert('Failed to approve task. Please try again.');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleRequestRevisions = (taskId) => {
    setTaskToAction(taskId);
    setRevisionNotes('');
    setShowRevisionModal(true);
  };

  const submitRevisions = async () => {
    if (!revisionNotes.trim()) {
      alert('Please provide revision notes');
      return;
    }

    try {
      setSubmittingAction(true);
      const token = localStorage.getItem('portal_token');
      
      await api.post(
        `/portal/${team_id}/tasks/${taskToAction}/request-revisions`,
        { notes: revisionNotes },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Refresh tasks
      await fetchTasks();
      setShowRevisionModal(false);
      setTaskToAction(null);
      setRevisionNotes('');
      alert('Revisions requested successfully!');
    } catch (error) {
      console.error('Error requesting revisions:', error);
      alert('Failed to request revisions. Please try again.');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleSendDeliverableComment = async () => {
    if (!deliverableComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      setSubmittingComment(true);
      const token = localStorage.getItem('portal_token');
      const commentText = deliverableComment.trim();
      
      // Capture video timestamp if video is present
      let timestamp = null;
      if (videoRef.current) {
        if (!videoRef.current.paused) {
          // If video is playing, pause it first
          videoRef.current.pause();
        }
        timestamp = Math.floor(videoRef.current.currentTime);
      }

      // Optimistically add comment to UI
      const optimisticId = `temp-${Date.now()}`;
      const optimisticComment = {
        id: optimisticId,
        content: commentText,
        user_name: contact?.email || 'You',
        created_at: new Date().toISOString(),
        video_timestamp: timestamp
      };

      setSelectedDeliverable((prev) => {
        if (!prev || prev.id !== selectedDeliverable?.id) return prev;
        const nextComments = [...(prev.comments || []), optimisticComment];
        return { ...prev, comments: nextComments };
      });

      setTasks((prev) =>
        prev.map((task) => {
          if (task.id !== currentTask?.id) return task;
          return {
            ...task,
            deliverables: (task.deliverables || []).map((deliverable) => {
              if (deliverable.id !== selectedDeliverable?.id) return deliverable;
              const nextComments = [...(deliverable.comments || []), optimisticComment];
              return { ...deliverable, comments: nextComments };
            })
          };
        })
      );
      setDeliverableComment('');
      
      // Add comment to the task (not just the deliverable)
      await api.post(
        `/portal/${team_id}/tasks/${currentTask.id}/comments`,
        { 
          comment: `Feedback on "${selectedDeliverable.title}":\n\n${commentText}`,
          deliverable_id: selectedDeliverable.id,
          timestamp: timestamp
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Clear comment and refresh tasks to show updated comments
      setDeliverableComment('');
      await fetchTasks();
    } catch (error) {
      console.error('Error submitting comment:', error);
      // Roll back optimistic comment
      setSelectedDeliverable((prev) => {
        if (!prev || prev.id !== selectedDeliverable?.id) return prev;
        const nextComments = (prev.comments || []).filter((c) => !String(c.id).startsWith('temp-'));
        return { ...prev, comments: nextComments };
      });
      setTasks((prev) =>
        prev.map((task) => {
          if (task.id !== currentTask?.id) return task;
          return {
            ...task,
            deliverables: (task.deliverables || []).map((deliverable) => {
              if (deliverable.id !== selectedDeliverable?.id) return deliverable;
              const nextComments = (deliverable.comments || []).filter((c) => !String(c.id).startsWith('temp-'));
              return { ...deliverable, comments: nextComments };
            })
          };
        })
      );
      setDeliverableComment(commentText);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      await api.post(`/portal/${team_id}/logout`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage
      localStorage.removeItem('portal_token');
      localStorage.removeItem('portal_team_id');
      localStorage.removeItem('portal_contact');
      localStorage.removeItem('portal_client');
      
      // Navigate to login
      navigate(`/portal/${team_id}`);
    }
  };

  const evaluateRule = (rule, values) => {
    const when = rule?.when || {};
    const actual = values?.[when.field];
    if ((when.op || 'equals') === 'equals') return String(actual) === String(when.value);
    if (when.op === 'not_equals') return String(actual) !== String(when.value);
    if (when.op === 'in') return Array.isArray(when.value) && when.value.map(String).includes(String(actual));
    return false;
  };

  const getRequestTypeFormConfig = () => {
    const cfgMap = portalSettings?.request_type_form_configs || {};
    if (!requestForm.request_type || !cfgMap || typeof cfgMap !== 'object') return null;

    // Exact key first
    if (cfgMap[requestForm.request_type]) return cfgMap[requestForm.request_type];

    // Fallback: normalized match (trim + case-insensitive)
    const target = String(requestForm.request_type).trim().toLowerCase();
    const matchedKey = Object.keys(cfgMap).find(
      (k) => String(k).trim().toLowerCase() === target
    );

    return matchedKey ? cfgMap[matchedKey] : null;
  };

  const resolveFieldOptions = (field) => {
    if (!field || field.type !== 'select') return [];

    let source = field.options_source || 'static';
    let ref = field.options_custom_field_id || field.options_custom_field_name || field.options_reference_table_id || field.options_reference_table_name || field.options_source_key;

    // Backward-compatible shorthand for older settings UI:
    // options: ["client_custom_field:post_type"] or ["client_field:post_type"]
    if ((!field.options_source || field.options_source === 'static') && Array.isArray(field.options) && field.options.length > 0) {
      const first = String(field.options[0] || '').trim();
      const prefixes = ['client_custom_field:', 'client_field:', '__client_field__:'];
      const matched = prefixes.find(p => first.startsWith(p));
      if (matched) {
        source = 'client_custom_field';
        ref = first.slice(matched.length).trim();
      }
      if (!matched) {
        const refPrefixes = ['reference_table:', 'ref_table:', '__reference_table__:'];
        const refMatched = refPrefixes.find(p => first.startsWith(p));
        if (refMatched) {
          source = 'reference_table';
          ref = first.slice(refMatched.length).trim();
        }
      }
    }

    if (source === 'client_custom_field') {
      if (!ref) return [];
      return clientCustomFieldOptions[String(ref)] || [];
    }

    if (source === 'reference_table') {
      if (!ref) return [];
      return referenceTableOptions[String(ref)] || [];
    }

    return field.options || [];
  };

  const getConditionalFieldLabel = (field) => {
    const humanize = (value) => String(value || '')
      .replace(/^client_custom\./i, '')
      .replace(/^client\./i, '')
      .replace(/^field[_-]?/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

    const rawLabel = String(field?.label || '').trim();
    const genericLabels = new Set(['conditional field', 'field', 'question', 'conditional question']);

    if (rawLabel && !genericLabels.has(rawLabel.toLowerCase())) {
      return rawLabel;
    }

    // If this field is sourced from a client custom value, prefer that configured key/name
    if ((field?.options_source || '') === 'client_custom_field') {
      const sourceRef = String(
        field?.options_custom_field_name ||
        field?.options_custom_field_id ||
        field?.options_source_key ||
        ''
      ).trim();
      if (sourceRef) return humanize(sourceRef);
    }

    // If sourced from a data table, prefer table ref over generated transient field ids
    if ((field?.options_source || '') === 'reference_table') {
      const tableRef = String(
        field?.options_reference_table_name ||
        field?.options_reference_table_id ||
        field?.options_source_key ||
        ''
      ).trim();
      if (tableRef) return humanize(tableRef);
    }

    const fallback = String(field?.name || field?.id || '').trim();
    if (!fallback) return 'Field';

    // Avoid showing opaque generated ids like field_1772813301078
    if (/^field[_-]?\d+$/i.test(fallback) || /^\d{8,}$/.test(fallback)) {
      return 'Custom Field';
    }

    return humanize(fallback) || 'Custom Field';
  };

  const getVisibleConditionalFields = (dynamicFields = requestForm.dynamic_fields) => {
    const cfg = getRequestTypeFormConfig();
    if (!cfg) return [];

    const values = {
      request_type: requestForm.request_type,
      title: requestForm.title,
      description: requestForm.description,
      ...(dynamicFields || {})
    };

    // Add client custom field values as condition sources (client_custom.<name>)
    Object.entries(clientCustomFieldValues || {}).forEach(([k, v]) => {
      values[`client_custom.${k}`] = v;
    });

    const visible = [...(cfg.base_fields || [])];
    (cfg.conditional_rules || []).forEach((rule) => {
      if (evaluateRule(rule, values)) {
        (rule.show_fields || []).forEach(f => visible.push(f));
        (rule.conditional_rules || []).forEach((nestedRule) => {
          if (evaluateRule(nestedRule, values)) {
            (nestedRule.show_fields || []).forEach(f => visible.push(f));
          }
        });
      }
    });

    const deduped = [];
    const seen = new Set();
    visible.forEach((f) => {
      const key = f?.id || f?.name;
      if (key && !seen.has(key)) {
        seen.add(key);
        deduped.push({ ...f, id: key, options: resolveFieldOptions(f) });
      }
    });
    return deduped;
  };

  const getVisibleRuleInstructions = () => {
    const cfg = getRequestTypeFormConfig();
    if (!cfg) return [];

    const values = {
      request_type: requestForm.request_type,
      title: requestForm.title,
      description: requestForm.description,
      ...((requestForm.items?.[0] || {}).dynamic_fields || requestForm.dynamic_fields || {})
    };
    Object.entries(clientCustomFieldValues || {}).forEach(([k, v]) => {
      values[`client_custom.${k}`] = v;
    });

    const notes = [];
    (cfg.conditional_rules || []).forEach((rule) => {
      if (evaluateRule(rule, values) && rule?.instructions && String(rule.instructions).trim()) {
        notes.push(String(rule.instructions).trim());
      }
    });

    return [...new Set(notes)];
  };

  const handleRequestFormChange = (field, value) => {
    setRequestForm(prev => {
      const next = { ...prev, [field]: value };
      // reset dynamic fields when request type changes
      if (field === 'request_type') {
        next.dynamic_fields = {};
        next.items = [makeRequestItem()];
      }
      return next;
    });
  };

  const handleItemDynamicFieldChange = (itemId, fieldId, value) => {
    setRequestForm(prev => ({
      ...prev,
      items: (prev.items || []).map(item =>
        item.id === itemId
          ? { ...item, dynamic_fields: { ...(item.dynamic_fields || {}), [fieldId]: value } }
          : item
      )
    }));
  };

  const addRequestItem = () => {
    setRequestForm(prev => ({
      ...prev,
      items: [...(prev.items || []), makeRequestItem()]
    }));
  };

  const removeRequestItem = (itemId) => {
    setRequestForm(prev => {
      const nextItems = (prev.items || []).filter(i => i.id !== itemId);
      const normalized = nextItems.length > 0 ? nextItems : [makeRequestItem()];
      return {
        ...prev,
        items: normalized,
        dynamic_fields: normalized[0]?.dynamic_fields || {}
      };
    });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setRequestForm(prev => ({ ...prev, attachments: files }));
  };

  const handleRemoveFile = (index) => {
    setRequestForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleApproveDeliverable = async (taskId, deliverableId) => {
    try {
      const token = localStorage.getItem('portal_token');
      await api.post(
        `/portal/${team_id}/tasks/${taskId}/deliverables/${deliverableId}/approve`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Refresh tasks to show updated status
      fetchTasks();
      alert('Deliverable approved successfully!');
    } catch (error) {
      console.error('Error approving deliverable:', error);
      alert('Failed to approve deliverable. Please try again.');
    }
  };

  const handleRequestRevision = async (taskId, deliverableId) => {
    const revisionNotes = prompt('Please describe what changes are needed:');
    if (!revisionNotes) return;
    
    try {
      const token = localStorage.getItem('portal_token');
      await api.post(
        `/portal/${team_id}/tasks/${taskId}/deliverables/${deliverableId}/request-revision`,
        { notes: revisionNotes },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Refresh tasks to show updated status
      fetchTasks();
      alert('Revision requested successfully!');
    } catch (error) {
      console.error('Error requesting revision:', error);
      alert('Failed to request revision. Please try again.');
    }
  };

  const confirmRecentSubmission = async (expectedName, title) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await api.get(`/portal/${team_id}/tasks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 10000
      });

      const tasks = response.data?.tasks || [];
      const nowMs = Date.now();

      return tasks.some((task) => {
        const taskName = task?.name || '';
        const createdAtMs = task?.created_at ? new Date(task.created_at).getTime() : null;
        const isRecent = createdAtMs ? (nowMs - createdAtMs) <= 5 * 60 * 1000 : false;
        const nameMatches = taskName === expectedName || (title && taskName.includes(title));
        return nameMatches && (isRecent || createdAtMs === null);
      });
    } catch (err) {
      return false;
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmittingRequest(true);
    setRequestError('');
    setRequestSuccess(false);

    try {
      const token = localStorage.getItem('portal_token');
      const formData = new FormData();

      const items = (requestForm.items && requestForm.items.length > 0)
        ? requestForm.items
        : [makeRequestItem()];

      // Client-side required validation for visible conditional fields per item
      for (let idx = 0; idx < items.length; idx += 1) {
        const item = items[idx];
        const visibleConditionalFields = getVisibleConditionalFields(item.dynamic_fields || {});
        const missing = visibleConditionalFields
          .filter(f => f?.required)
          .filter(f => {
            const v = item.dynamic_fields?.[f.id];
            return v === undefined || v === null || String(v).trim() === '';
          })
          .map(f => getConditionalFieldLabel(f));

        if (missing.length > 0) {
          setRequestError(`Request ${idx + 1}: Please complete required fields: ${missing.join(', ')}`);
          setSubmittingRequest(false);
          return;
        }
      }
      
      formData.append('request_type', requestForm.request_type);
      formData.append('title', requestForm.title);
      formData.append('description', requestForm.description);

      const primaryDynamicFields = items[0]?.dynamic_fields || {};
      Object.entries(primaryDynamicFields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          formData.append(key, value);
        }
      });

      const itemsPayload = items.map((item, index) => ({
        index,
        dynamic_fields: item.dynamic_fields || {}
      }));
      formData.append('items_json', JSON.stringify(itemsPayload));
      
      requestForm.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
      });

      await api.post(`/portal/${team_id}/submit-request`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setRequestSuccess(true);
      // Reset form
      setRequestForm({
        request_type: '',
        title: '',
        description: '',
        dynamic_fields: {},
        items: [makeRequestItem()],
        attachments: []
      });
      
      // Switch to tasks tab after 2 seconds
      setTimeout(() => {
        setActiveTab('tasks');
        setRequestSuccess(false);
        fetchTasks(); // Refresh tasks
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting request:', err);
      const expectedName = `[${requestForm.request_type}] ${requestForm.title}`;
      const confirmed = await confirmRecentSubmission(expectedName, requestForm.title);
      if (confirmed) {
        setRequestSuccess(true);
        setRequestError('');
        setTimeout(() => {
          setActiveTab('tasks');
          setRequestSuccess(false);
          fetchTasks();
        }, 2000);
      } else {
        setRequestError(err.response?.data?.detail || err.response?.data?.error || 'Failed to submit request. Please try again.');
      }
    } finally {
      setSubmittingRequest(false);
    }
  };

  const getStatusColor = (portalStatus) => {
    switch (portalStatus) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'in_review':
        return 'bg-amber-100 text-amber-800 border border-amber-200';
      case 'incomplete':
        return 'bg-purple-100 text-purple-800 border border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusIcon = (portalStatus) => {
    switch (portalStatus) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'in_review':
        return <Clock className="w-4 h-4" />;
      case 'incomplete':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (portalStatus) => {
    // Use custom stage names from settings if available
    if (portalSettings) {
      switch (portalStatus) {
        case 'approved':
          return portalSettings.approved_stage_name || 'Complete';
        case 'in_review':
          return portalSettings.client_review_stage_name || 'In Review';
        case 'incomplete':
          return portalSettings.revision_stage_name || 'In Progress';
        default:
          return portalStatus;
      }
    }
    
    switch (portalStatus) {
      case 'approved':
        return 'Complete';
      case 'in_review':
        return 'In Review';
      case 'incomplete':
        return 'In Progress';
      default:
        return portalStatus;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Brand Logo & Name */}
            <div className="flex items-center space-x-3">
              <img 
                src={branding?.logo_url || "/images/athena-logo-wide.png"} 
                alt={branding?.brand_name || "Athena"} 
                className="h-8 w-auto max-w-[180px] object-contain"
              />
              <span 
                className="text-xl font-bold whitespace-nowrap"
                style={{ 
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  backgroundImage: branding?.primary_color && branding?.secondary_color 
                    ? `linear-gradient(to right, ${branding.primary_color}, ${branding.secondary_color})`
                    : 'linear-gradient(to right, rgb(147, 51, 234), rgb(219, 39, 119))',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  display: 'inline-block'
                }}
              >
                Athena Project Management
              </span>
            </div>

            {/* Center: Organization Name */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">
                {portalSettings?.organization_name || branding?.brand_name || 'Athena'}
              </h1>
            </div>

            {/* Right: User Actions */}
            <div className="flex items-center space-x-4">
              {contact && (
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <User className="w-4 h-4" />
                  <span>{contact.name}</span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Client Info Bar */}
      <div 
        className="border-b border-gray-200"
        style={{ 
          background: branding?.primary_color 
            ? `linear-gradient(to right, ${branding.primary_color}10, ${branding.secondary_color || branding.primary_color}10)` 
            : 'linear-gradient(to right, rgb(237, 233, 254), rgb(252, 231, 243))'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <div 
              className="p-2 rounded-lg"
              style={{ background: getBrandGradient() }}
            >
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {client?.name || 'Client Portal'}
              </h2>
              <p className="text-sm text-gray-600">Project Dashboard</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('tasks')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tasks'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <CheckSquare className="w-4 h-4" />
                <span>Open Tasks</span>
              </div>
            </button>
            
            {portalSettings?.enable_request_form && (
              <button
                onClick={() => setActiveTab('request')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'request'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Send className="w-4 h-4" />
                  <span>Submit Request</span>
                </div>
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Open Tasks Tab */}
        {activeTab === 'tasks' && (
          <div>
            {/* Welcome Message */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {contact?.name?.split(' ')[0] || 'there'}! 👋
              </h2>
              <p className="text-gray-600">
                Here's an overview of your projects and tasks
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Total Tasks</span>
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">{tasks.length}</div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">{getStatusLabel('incomplete')}</span>
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {tasks.filter(t => t.portal_status === 'incomplete').length}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">{getStatusLabel('approved')}</span>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {tasks.filter(t => t.portal_status === 'approved').length}
                </div>
              </div>
            </div>

            {/* Tasks List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Your Tasks</h3>
              </div>

              {tasks.length === 0 ? (
                <div className="p-12 text-center">
                  <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">No tasks available</p>
                  <p className="text-sm text-gray-500">Check back later for updates</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className="border-b border-gray-200 last:border-b-0"
                    >
                      {/* Collapsed View */}
                      <div
                        onClick={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                        className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-lg font-semibold text-gray-900">
                                {task.name}
                              </h4>
                              <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(task.portal_status)}`}>
                                {getStatusIcon(task.portal_status)}
                                <span>{getStatusLabel(task.portal_status)}</span>
                              </span>
                            </div>

                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              {task.project_name && (
                                <span className="flex items-center">
                                  <Briefcase className="w-4 h-4 mr-1" />
                                  {task.project_name}
                                </span>
                              )}
                              {portalSettings?.show_task_due_date && task.due_date && (
                                <span className="flex items-center">
                                  <Clock className="w-4 h-4 mr-1" />
                                  Due: {new Date(task.due_date).toLocaleDateString()}
                                </span>
                              )}
                              {task.deliverables && task.deliverables.length > 0 && (
                                <span className="flex items-center text-purple-600">
                                  <FileText className="w-4 h-4 mr-1" />
                                  {task.deliverables.length} deliverable{task.deliverables.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            
                            {/* Task Action Buttons (Option A - Collapsed View) */}
                            {task.portal_status === 'in_review' && (
                              <div className="flex items-center gap-2 mt-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveTask(task.id);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-all"
                                  style={{
                                    background: getBrandGradient(),
                                  }}
                                >
                                  <CheckSquare className="w-4 h-4" />
                                  Approve
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestRevisions(task.id);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Request Revisions
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Expand/Collapse Icon */}
                          <button className="ml-4 text-gray-400 hover:text-gray-600">
                            {expandedTaskId === task.id ? (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Expanded View */}
                      {expandedTaskId === task.id && (
                        <div className="px-6 pb-6 bg-gray-50">
                          {/* Full Description */}
                          {portalSettings?.show_task_description && task.description && (
                            <div className="mb-6">
                              <h5 className="text-sm font-semibold text-gray-900 mb-2">Description</h5>
                              <div 
                                className="text-gray-700 text-sm leading-relaxed [&>div]:mb-2 [&>p]:mb-2 [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 [&>li]:mb-1"
                                dangerouslySetInnerHTML={{ __html: task.description.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') }} 
                              />
                            </div>
                          )}

                          {/* Task Review Notes */}
                          {task.review_notes && (
                            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                              <div className="flex items-start gap-3">
                                <MessageSquare className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-purple-900 mb-2">Task Notes</p>
                                  <p className="text-sm text-purple-800 whitespace-pre-wrap">{task.review_notes}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Uploads Section */}
                          {portalSettings?.show_task_attachments && task.attachments && task.attachments.length > 0 && (
                            <div className="mb-6">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3">
                                Uploads ({task.attachments.length})
                              </h5>
                              <div className="space-y-2">
                                {task.attachments.map((file) => (
                                  <a
                                    key={file.id}
                                    href={file.file_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-all"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span className="truncate">{file.filename}</span>
                                    <span className="text-xs text-gray-500">View</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Deliverables Section - Compact Cards */}
                          {task.deliverables && task.deliverables.length > 0 && (
                            <div className="mb-6">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3">
                                Deliverables ({task.deliverables.length})
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {task.deliverables.map((deliverable) => {
                                  // Check for image/video with query params support
                                  const isImage = deliverable.file_url && /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(deliverable.file_url);
                                  const isVideo = deliverable.file_url && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(deliverable.file_url);
                                  
                                  // Extract domain for favicon
                                  let faviconUrl = null;
                                  if (!isImage && !isVideo && deliverable.file_url) {
                                    try {
                                      const url = new URL(deliverable.file_url);
                                      faviconUrl = `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
                                    } catch (e) {
                                      // Invalid URL, will show file icon
                                    }
                                  }
                                  
                                  return (
                                    <button
                                      key={deliverable.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDeliverable(deliverable);
                                        setCurrentTask(task);
                                      }}
                                      className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-purple-400 hover:shadow-md transition-all text-left group"
                                    >
                                      {/* Thumbnail Preview */}
                                      <div className={`mb-3 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center ${isImage || isVideo ? 'min-h-[8rem] max-h-[18rem]' : 'h-32'}`}>
                                        {isImage ? (
                                          <img 
                                            src={deliverable.file_url} 
                                            alt={deliverable.title}
                                            className="w-full h-auto max-h-[18rem] object-contain"
                                          />
                                        ) : isVideo ? (
                                          <div className="w-full h-auto relative bg-black/5">
                                            <video
                                              src={deliverable.file_url}
                                              className="w-full h-auto max-h-[18rem] object-contain"
                                              muted
                                              preload="metadata"
                                              playsInline
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                              <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                                                <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                  <path d="M8 6.5v7l6-3.5-6-3.5z" />
                                                </svg>
                                              </div>
                                            </div>
                                          </div>
                                        ) : faviconUrl ? (
                                          <div className="flex flex-col items-center justify-center gap-2">
                                            <img 
                                              src={faviconUrl} 
                                              alt="Site favicon"
                                              className="w-16 h-16"
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                              }}
                                            />
                                            <FileText className="w-12 h-12 text-gray-400" style={{display: 'none'}} />
                                          </div>
                                        ) : (
                                          <FileText className="w-12 h-12 text-gray-400" />
                                        )}
                                      </div>
                                      
                                      {/* Info */}
                                      <div className="flex items-start gap-2 mb-1">
                                        <h6 className="text-sm font-semibold text-gray-900 line-clamp-2 flex-1">{deliverable.title}</h6>
                                        {deliverable.status === 'approved' && (
                                          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                        )}
                                      </div>
                                      
                                      {deliverable.review_notes && (
                                        <div className="flex items-center gap-1 text-xs text-amber-700 mb-2">
                                          <MessageSquare className="w-3 h-3" />
                                          <span>Has review notes</span>
                                        </div>
                                      )}
                                      
                                      <div className="text-xs text-purple-600 font-medium group-hover:text-purple-700">
                                        Click to review →
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Task Action Buttons (Option B - Expanded View Bottom) */}
                          {task.portal_status === 'in_review' && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                              <div className="flex items-center justify-end gap-3">
                                <button
                                  onClick={() => handleRequestRevisions(task.id)}
                                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Request Revisions
                                </button>
                                <button
                                  onClick={() => handleApproveTask(task.id)}
                                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all hover:shadow-lg"
                                  style={{
                                    background: getBrandGradient(),
                                  }}
                                >
                                  <CheckSquare className="w-4 h-4" />
                                  Approve Task
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submit Request Tab */}
        {activeTab === 'request' && portalSettings?.enable_request_form && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {portalSettings.form_title || 'Submit a Request'}
                </h2>
                {portalSettings.form_description && (
                  <p className="text-gray-600">
                    {portalSettings.form_description}
                  </p>
                )}
              </div>

              {requestSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-800 font-medium">Request submitted successfully!</p>
                    <p className="text-sm text-green-700 mt-1">We'll get back to you soon.</p>
                  </div>
                </div>
              )}

              {requestError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{requestError}</p>
                </div>
              )}

              <form onSubmit={handleSubmitRequest} className="space-y-6">
                {/* Request Type */}
                {(() => {
                  // Use client-specific allowed types if available, otherwise use all company types
                  const availableTypes = clientAllowedRequestTypes !== null 
                    ? clientAllowedRequestTypes 
                    : (portalSettings.request_types || []);
                  
                  return availableTypes.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Request Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={requestForm.request_type}
                        onChange={(e) => handleRequestFormChange('request_type', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select a type...</option>
                        {availableTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {(() => {
                  const ruleNotes = getVisibleRuleInstructions();
                  if (ruleNotes.length > 0) {
                    return ruleNotes.map((note, idx) => (
                      <div key={`rule-note-${idx}`} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                        {note}
                      </div>
                    ));
                  }

                  const cfg = getRequestTypeFormConfig();
                  const instructions = cfg?.instructions;
                  if (!instructions || !String(instructions).trim()) return null;
                  return (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
                      {instructions}
                    </div>
                  );
                })()}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={requestForm.title}
                    onChange={(e) => handleRequestFormChange('title', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Brief summary of your request"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={requestForm.description}
                    onChange={(e) => handleRequestFormChange('description', e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Provide detailed information about your request..."
                    required
                  />
                </div>

                {/* Conditional Request-Type Fields (supports multiple same-type request items) */}
                {getVisibleConditionalFields((requestForm.items?.[0] || {}).dynamic_fields || {}).length > 0 && (
                  <div className="space-y-4">
                    {(requestForm.items || []).map((item, itemIndex) => {
                      const visibleFields = getVisibleConditionalFields(item.dynamic_fields || {});
                      return (
                        <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            {(requestForm.items || []).length > 1 ? (
                              <p className="text-sm font-medium text-gray-800">Request {itemIndex + 1}</p>
                            ) : <span />}
                            {itemIndex > 0 && (
                              <button type="button" onClick={() => removeRequestItem(item.id)} className="text-xs text-red-600">Remove Request</button>
                            )}
                          </div>

                          <div className="space-y-4">
                            {visibleFields.map((field) => {
                              const fieldId = field.id || field.name;
                              const value = item.dynamic_fields?.[fieldId] || '';
                              const isRequired = !!field.required;

                              if (field.type === 'textarea') {
                                return (
                                  <div key={`${item.id}-${fieldId}`}>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      {getConditionalFieldLabel(field)} {isRequired && <span className="text-red-500">*</span>}
                                    </label>
                                    <textarea
                                      value={value}
                                      onChange={(e) => handleItemDynamicFieldChange(item.id, fieldId, e.target.value)}
                                      rows={4}
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                      placeholder={field.placeholder || ''}
                                      required={isRequired}
                                    />
                                  </div>
                                );
                              }

                              if (field.type === 'select') {
                                return (
                                  <div key={`${item.id}-${fieldId}`}>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      {getConditionalFieldLabel(field)} {isRequired && <span className="text-red-500">*</span>}
                                    </label>
                                    <select
                                      value={value}
                                      onChange={(e) => handleItemDynamicFieldChange(item.id, fieldId, e.target.value)}
                                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                      required={isRequired}
                                    >
                                      <option value="">Select...</option>
                                      {(field.options || []).map((opt, idx) => (
                                        <option key={`${item.id}-${fieldId}-${idx}`} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  </div>
                                );
                              }

                              return (
                                <div key={`${item.id}-${fieldId}`}>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {getConditionalFieldLabel(field)} {isRequired && <span className="text-red-500">*</span>}
                                  </label>
                                  <input
                                    type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : 'text'}
                                    value={value}
                                    onChange={(e) => handleItemDynamicFieldChange(item.id, fieldId, e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder={field.placeholder || ''}
                                    required={isRequired}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      onClick={addRequestItem}
                      className="px-3 py-2 text-sm border border-dashed border-purple-400 text-purple-700 rounded-lg hover:bg-purple-50"
                    >
                      + Add additional request
                    </button>
                  </div>
                )}

                {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachments
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Paperclip className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        Click to upload files or drag and drop
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        PDF, DOC, PNG, JPG up to 10MB
                      </span>
                    </label>
                  </div>
                  
                  {requestForm.attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {requestForm.attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-700">{file.name}</span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submittingRequest}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submittingRequest ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>

      {/* Deliverable Review Modal */}
      {selectedDeliverable && (() => {
        const isImage = selectedDeliverable.file_url && /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(selectedDeliverable.file_url);
        const isVideo = selectedDeliverable.file_url && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(selectedDeliverable.file_url);
        const hasMedia = isImage || isVideo;
        
        // Adaptive sizing: larger for media, smaller for documents
        const modalWidth = hasMedia ? 'max-w-[95vw]' : 'max-w-3xl';
        
        const handleMouseDown = (e) => {
          if (hasMedia) {
            setIsDraggingDivider(true);
            e.preventDefault();
          }
        };
        
        const handleMouseMove = (e) => {
          if (isDraggingDivider && hasMedia) {
            const modal = e.currentTarget.closest('.modal-container');
            if (modal) {
              const rect = modal.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percentage = (x / rect.width) * 100;
              // Constrain between 50% and 85%
              const constrainedPercentage = Math.min(Math.max(percentage, 50), 85);
              setDividerPosition(constrainedPercentage);
            }
          }
        };
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedDeliverable(null)}>
          <div 
            className={`modal-container bg-white rounded-xl shadow-2xl ${modalWidth} w-full max-h-[90vh] overflow-hidden ${isDraggingDivider ? 'select-none' : ''}`}
            onClick={(e) => e.stopPropagation()}
            onMouseMove={handleMouseMove}
            onMouseUp={() => setIsDraggingDivider(false)}
            onMouseLeave={() => setIsDraggingDivider(false)}
          >
            <div className={`flex h-full max-h-[90vh] ${hasMedia ? 'flex-row' : 'flex-col'}`}>
              {/* Left: Media Display (resizable width for media, hidden for documents) */}
              {hasMedia && (
              <div 
                className="bg-gray-900 p-8 flex flex-col items-center justify-center min-h-[400px] lg:min-h-0"
                style={{ width: `${dividerPosition}%` }}
              >
                {(() => {
                  if (isImage) {
                    return (
                      <img 
                        src={selectedDeliverable.file_url} 
                        alt={selectedDeliverable.title}
                        className="max-w-full max-h-full object-contain"
                      />
                    );
                  } else if (isVideo) {
                    return (
                      <video 
                        ref={videoRef}
                        src={selectedDeliverable.file_url} 
                        controls
                        className="max-w-full max-h-full"
                      >
                        Your browser does not support the video tag.
                      </video>
                    );
                  } else {
                    return (
                      <div className="text-center">
                        <FileText className="w-20 h-20 text-gray-500 mx-auto mb-4" />
                        <p className="text-gray-400 mb-4">File preview not available</p>
                        <a
                          href={selectedDeliverable.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all"
                          style={{
                            background: getBrandGradient()
                          }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Open File
                        </a>
                      </div>
                    );
                  }
                })()}
              </div>
              )}
              
              {/* Resizable Divider */}
              {hasMedia && (
                <div
                  className="w-1 bg-gray-300 hover:bg-purple-500 cursor-col-resize transition-colors relative group"
                  onMouseDown={handleMouseDown}
                >
                  <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-purple-500/20" />
                </div>
              )}
              
              {/* Right: Info & Actions (resizable width for media, full width for documents) */}
              <div 
                className="flex flex-col max-h-[90vh] lg:max-h-none"
                style={hasMedia ? { width: `${100 - dividerPosition}%` } : {}}
              >
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900 flex-1 pr-4">{selectedDeliverable.title}</h3>
                    <button
                      onClick={() => setSelectedDeliverable(null)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {selectedDeliverable.description && (
                    <p className="text-sm text-gray-600">{selectedDeliverable.description}</p>
                  )}
                </div>
                
                {/* Link Deliverable - Open in New Tab Button */}
                {!hasMedia && selectedDeliverable.file_url && (
                  <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">External Link</p>
                          <p className="text-xs text-gray-500 truncate max-w-xs">{selectedDeliverable.file_url}</p>
                        </div>
                      </div>
                      <a
                        href={selectedDeliverable.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-lg transition-all hover:shadow-lg"
                        style={{
                          background: getBrandGradient(),
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open Link
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Review Notes */}
                  {selectedDeliverable.review_notes && (
                    <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-amber-900 mb-1">Review Notes</p>
                          <p className="text-sm text-amber-800 whitespace-pre-wrap">{selectedDeliverable.review_notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Comments Section */}
                  {portalSettings?.show_task_comments && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-purple-600" />
                        Comments
                      </h4>
                      
                      <div>
                        {/* Conversation History */}
                        {selectedDeliverable.comments && selectedDeliverable.comments.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Conversation</h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto p-3 bg-gray-50 rounded-lg border border-gray-200">
                              {selectedDeliverable.comments.map((comment) => {
                                const formatTimestamp = (seconds) => {
                                  if (!seconds && seconds !== 0) return null;
                                  const mins = Math.floor(seconds / 60);
                                  const secs = seconds % 60;
                                  return `${mins}:${secs.toString().padStart(2, '0')}`;
                                };
                                
                                const handleTimestampClick = (seconds) => {
                                  if (videoRef.current) {
                                    videoRef.current.currentTime = seconds;
                                    videoRef.current.play();
                                  }
                                };
                                
                                return (
                                  <div key={comment.id} className="bg-white rounded-lg p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs font-semibold text-purple-600">{comment.user_name}</span>
                                      <span className="text-xs text-gray-400">•</span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(comment.created_at).toLocaleString()}
                                      </span>
                                      {comment.video_timestamp != null && (
                                        <>
                                          <span className="text-xs text-gray-400">•</span>
                                          <button
                                            onClick={() => handleTimestampClick(comment.video_timestamp)}
                                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                          >
                                            {formatTimestamp(comment.video_timestamp)}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment.content}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Add New Feedback */}
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Leave Feedback</h4>
                        <textarea
                          value={deliverableComment}
                          onChange={(e) => setDeliverableComment(e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm"
                          rows="3"
                          placeholder="Add a comment about this deliverable..."
                        ></textarea>
                        <button
                          onClick={handleSendDeliverableComment}
                          disabled={submittingComment || !deliverableComment.trim()}
                          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            background: getBrandGradient()
                          }}
                        >
                          <Send className="w-4 h-4" />
                          {submittingComment ? 'Sending...' : 'Send Comment'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
      
      {/* Approve Confirmation Modal */}
      {showApproveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Approve Task?</h3>
              <p className="text-gray-600">
                Are you sure you want to approve this task? This will mark it as complete and notify the team.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowApproveConfirm(false);
                  setTaskToAction(null);
                }}
                disabled={submittingAction}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproveTask}
                disabled={submittingAction}
                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
                style={{
                  background: getBrandGradient(),
                }}
              >
                {submittingAction ? 'Approving...' : 'Approve Task'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Request Revisions Modal */}
      {showRevisionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Request Revisions</h3>
              <p className="text-gray-600 mb-4">
                Please provide detailed feedback about what needs to be revised.
              </p>
              <textarea
                value={revisionNotes}
                onChange={(e) => setRevisionNotes(e.target.value)}
                placeholder="Describe the changes needed..."
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              {!revisionNotes.trim() && (
                <p className="text-sm text-red-600 mt-2">* Revision notes are required</p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRevisionModal(false);
                  setTaskToAction(null);
                  setRevisionNotes('');
                }}
                disabled={submittingAction}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitRevisions}
                disabled={submittingAction || !revisionNotes.trim()}
                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50"
                style={{
                  background: getBrandGradient(),
                }}
              >
                {submittingAction ? 'Submitting...' : 'Submit Revisions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPortalDashboard;
